import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  sequences,
  type AiTone,
  type SequenceIndustry,
  type SequenceStep,
  type SequenceSummary,
} from "@/lib/api";
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Mail,
  Calendar,
  Target,
  Clock,
  ChevronDown,
  ChevronUp,
  Trash2,
  Zap,
  Send,
  Eye,
} from "lucide-react";

// ── Constants ─────────────────────────────────────────────────────────────────

const INDUSTRIES: { value: SequenceIndustry; label: string; emoji: string }[] = [
  { value: "general",        label: "General",         emoji: "📧" },
  { value: "ecommerce",      label: "E-commerce",      emoji: "🛍️" },
  { value: "saas",           label: "SaaS",            emoji: "💻" },
  { value: "real_estate",    label: "Real Estate",     emoji: "🏠" },
  { value: "local_services", label: "Local Services",  emoji: "🔧" },
  { value: "agency",         label: "Agency",          emoji: "🎯" },
  { value: "education",      label: "Education",       emoji: "📚" },
  { value: "health",         label: "Health",          emoji: "🏥" },
];

const TONES: { value: AiTone; label: string }[] = [
  { value: "professional", label: "Professional" },
  { value: "casual",       label: "Casual" },
  { value: "friendly",     label: "Friendly" },
  { value: "urgent",       label: "Urgent" },
  { value: "playful",      label: "Playful" },
];

const EXAMPLE_PROMPTS = [
  "Create a 7-day Christmas sale campaign for a skincare brand",
  "5-day SaaS onboarding welcome sequence for new trial users",
  "3-email HVAC lead nurturing campaign for homeowners",
  "One week product launch campaign for a fitness app",
  "Re-engagement campaign for inactive ecommerce subscribers",
  "4-day Black Friday countdown for an electronics store",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function inputClass(hasError = false) {
  return `w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 bg-paper ${
    hasError ? "border-red-400" : "border-black/[0.08]"
  }`;
}

function EmailTypeIcon({ type }: { type: string }) {
  const map: Record<string, string> = {
    announcement: "📢", social_proof: "⭐", urgency: "⏳", reminder: "🔔",
    last_call: "🚨", welcome: "👋", follow_up: "📩", education: "📖",
    thank_you: "🙏", re_engage: "🔄", promotion: "🎁",
  };
  return <span className="text-lg">{map[type] || "📧"}</span>;
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AiCampaignGeneratorPage() {
  const navigate = useNavigate();

  // State
  const [phase, setPhase] = useState<"prompt" | "generating" | "result">("prompt");
  const [prompt, setPrompt] = useState("");
  const [industry, setIndustry] = useState<SequenceIndustry>("general");
  const [tone, setTone] = useState<AiTone>("professional");
  const [brandName, setBrandName] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Result
  const [sequence, setSequence] = useState<SequenceSummary | null>(null);
  const [steps, setSteps] = useState<SequenceStep[]>([]);
  const [expandedStep, setExpandedStep] = useState<number | null>(0);

  // Refs
  const generateInFlight = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Generate ──────────────────────────────────────────────────────────────

  async function handleGenerate() {
    if (!prompt.trim() || prompt.trim().length < 10) {
      setError("Please describe the campaign you want (at least 10 characters)");
      return;
    }
    if (generateInFlight.current) return;
    generateInFlight.current = true;
    setError(null);
    setPhase("generating");

    try {
      const res = await sequences.generate({
        prompt: prompt.trim(),
        industry,
        tone,
        brandName: brandName.trim() || undefined,
        ctaUrl: ctaUrl.trim() || undefined,
      });
      setSequence(res.sequence);
      setSteps(res.steps);
      setExpandedStep(0);
      setPhase("result");
    } catch (e: any) {
      setError(e.message || "Failed to generate campaign. Please try again.");
      setPhase("prompt");
    } finally {
      generateInFlight.current = false;
    }
  }

  // ── Delete sequence & go back ─────────────────────────────────────────────

  async function handleDiscard() {
    if (sequence?.id) {
      try { await sequences.delete(sequence.id); } catch { /* ignore */ }
    }
    setSequence(null);
    setSteps([]);
    setError(null);
    setPhase("prompt");
  }

  // ── Use example prompt ────────────────────────────────────────────────────

  function useExample(text: string) {
    setPrompt(text);
    textareaRef.current?.focus();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button
          onClick={() => navigate("/campaigns")}
          className="p-2 rounded-lg hover:bg-black/[0.04] transition-colors"
        >
          <ArrowLeft size={18} className="text-slate" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-ink flex items-center gap-2">
            <Sparkles size={22} className="text-coral" />
            AI Campaign Generator
          </h1>
          <p className="text-sm text-slate mt-0.5">
            Describe what you want and we'll build the entire multi-step campaign for you.
          </p>
        </div>
      </div>

      {/* ═══════ PROMPT PHASE ═══════ */}
      {phase === "prompt" && (
        <div className="space-y-6">
          {/* Big prompt input */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-2">
              What campaign do you want to create?
            </label>
            <textarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => { setPrompt(e.target.value); setError(null); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleGenerate();
              }}
              placeholder="e.g. Create me a one week Christmas email campaign for an ecommerce store..."
              rows={4}
              className={`w-full px-5 py-4 rounded-2xl border-2 text-base font-body leading-relaxed
                focus:outline-none focus:ring-4 focus:ring-coral/15 focus:border-coral
                bg-white placeholder-slate/50 resize-none transition-all ${
                error ? "border-red-400" : "border-black/[0.08]"
              }`}
            />
            <div className="absolute bottom-3 right-3 flex items-center gap-2">
              <span className="text-xs text-slate/60">
                {prompt.length > 0 ? `${prompt.length} chars` : "⌘+Enter to generate"}
              </span>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-500 -mt-3">{error}</p>
          )}

          {/* Example prompts */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate mb-2">Try an example</p>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_PROMPTS.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => useExample(ex)}
                  className="px-3 py-1.5 rounded-full bg-coral/5 border border-coral/15 text-xs
                    font-medium text-coral hover:bg-coral/10 hover:border-coral/30 transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          {/* Industry pills */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-2">
              Industry
            </label>
            <div className="flex flex-wrap gap-2">
              {INDUSTRIES.map((ind) => (
                <button
                  key={ind.value}
                  onClick={() => setIndustry(ind.value)}
                  className={`px-3.5 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    industry === ind.value
                      ? "bg-coral text-white border-coral shadow-sm"
                      : "bg-white border-black/[0.08] text-ink hover:border-black/20"
                  }`}
                >
                  {ind.emoji} {ind.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tone pills */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-2">
              Tone
            </label>
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTone(t.value)}
                  className={`px-3.5 py-2 rounded-xl text-sm font-medium border transition-colors ${
                    tone === t.value
                      ? "bg-coral text-white border-coral shadow-sm"
                      : "bg-white border-black/[0.08] text-ink hover:border-black/20"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Advanced options toggle */}
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate hover:text-ink transition-colors"
          >
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Advanced Options
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-2 gap-4 -mt-2">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                  Brand Name
                </label>
                <input
                  type="text"
                  placeholder="Your brand name"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className={inputClass()}
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1.5">
                  CTA URL
                </label>
                <input
                  type="url"
                  placeholder="https://yoursite.com/offer"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  className={inputClass()}
                />
              </div>
            </div>
          )}

          {/* Generate button */}
          <div className="pt-2 flex justify-center">
            <button
              onClick={handleGenerate}
              disabled={!prompt.trim()}
              className="inline-flex items-center gap-2.5 px-10 py-4 rounded-full
                bg-coral text-white font-bold text-base
                hover:bg-coral-dark disabled:opacity-40 disabled:cursor-not-allowed
                shadow-lg shadow-coral/25 hover:shadow-xl hover:shadow-coral/30
                transition-all active:scale-[0.98]"
            >
              <Zap size={18} />
              Generate Campaign
            </button>
          </div>
        </div>
      )}

      {/* ═══════ GENERATING PHASE ═══════ */}
      {phase === "generating" && (
        <div className="flex flex-col items-center justify-center py-20 space-y-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-coral/10 flex items-center justify-center">
              <Loader2 size={32} className="animate-spin text-coral" />
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-coral flex items-center justify-center">
              <Sparkles size={12} className="text-white" />
            </div>
          </div>
          <div className="text-center space-y-2">
            <h2 className="text-xl font-display font-bold text-ink">Building your campaign...</h2>
            <p className="text-sm text-slate max-w-md">
              Our AI is planning the campaign structure, writing each email,
              and assembling ready-to-send templates. This takes 15–30 seconds.
            </p>
          </div>
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-2.5 h-2.5 rounded-full bg-coral/30"
                style={{
                  animation: "pulse 1.5s ease-in-out infinite",
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ═══════ RESULT PHASE ═══════ */}
      {phase === "result" && sequence && (
        <div className="space-y-6">
          {/* Sequence header card */}
          <div className="p-6 rounded-2xl bg-white border border-black/[0.06] shadow-sm">
            <div className="flex items-start justify-between">
              <div className="space-y-1.5">
                <h2 className="text-xl font-display font-bold text-ink">
                  {sequence.name}
                </h2>
                <p className="text-sm text-slate">{sequence.description}</p>
                <div className="flex flex-wrap gap-3 mt-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-coral/5 text-xs font-semibold text-coral">
                    <Calendar size={12} /> {sequence.durationDays} days
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal/10 text-xs font-semibold text-teal">
                    <Mail size={12} /> {steps.length} emails
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/[0.04] text-xs font-semibold text-slate">
                    <Target size={12} /> {sequence.industry}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-50 text-xs font-bold text-green-700 border border-green-200">
                  <Check size={12} /> Created as drafts
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate mb-4">
              Campaign Timeline
            </h3>
            <div className="space-y-3">
              {steps.map((step, i) => (
                <div key={step.id} className="relative">
                  {/* Connector line */}
                  {i < steps.length - 1 && (
                    <div className="absolute left-[22px] top-[52px] w-0.5 h-[calc(100%-20px)] bg-black/[0.06]" />
                  )}

                  <div
                    className={`relative rounded-2xl border transition-all cursor-pointer ${
                      expandedStep === i
                        ? "bg-white border-coral/30 shadow-md shadow-coral/5"
                        : "bg-white border-black/[0.06] hover:border-black/15 hover:shadow-sm"
                    }`}
                    onClick={() => setExpandedStep(expandedStep === i ? null : i)}
                  >
                    {/* Step header */}
                    <div className="flex items-center gap-4 p-4">
                      {/* Day badge */}
                      <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center shrink-0 ${
                        expandedStep === i
                          ? "bg-coral text-white"
                          : "bg-black/[0.04] text-slate"
                      }`}>
                        <span className="text-[10px] font-bold uppercase leading-none">Day</span>
                        <span className="text-sm font-bold leading-none">{step.day}</span>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <EmailTypeIcon type={step.emailType} />
                          <span className="text-xs font-bold uppercase tracking-wider text-slate">
                            {step.emailType.replace(/_/g, " ")}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-ink mt-0.5 truncate">
                          {step.subject}
                        </p>
                      </div>

                      {/* Meta */}
                      <div className="hidden sm:flex items-center gap-3 shrink-0">
                        <span className="inline-flex items-center gap-1 text-xs text-slate">
                          <Clock size={11} /> {step.sendTime}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-slate">
                          <Target size={11} /> {step.goal}
                        </span>
                      </div>

                      {/* Expand indicator */}
                      <div className="shrink-0">
                        {expandedStep === i
                          ? <ChevronUp size={16} className="text-coral" />
                          : <ChevronDown size={16} className="text-slate" />
                        }
                      </div>
                    </div>

                    {/* Expanded content */}
                    {expandedStep === i && (
                      <div className="px-4 pb-4 pt-0 border-t border-black/[0.04]">
                        <div className="grid grid-cols-2 gap-4 mt-4 mb-4">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-slate mb-1">Preview Text</p>
                            <p className="text-sm text-ink">{step.previewText || "—"}</p>
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-slate mb-1">CTA Button</p>
                            <p className="text-sm text-ink">{step.callToAction || "—"}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/campaigns`);
                            }}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                              bg-coral text-white text-xs font-semibold
                              hover:bg-coral-dark transition-colors"
                          >
                            <Send size={12} /> Open in Campaigns
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/campaigns`);
                            }}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                              border border-black/[0.08] text-xs font-semibold text-slate
                              hover:border-black/20 transition-colors"
                          >
                            <Eye size={12} /> Edit Draft
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom actions */}
          <div className="flex items-center justify-between pt-4 border-t border-black/[0.06]">
            <button
              onClick={handleDiscard}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full
                border border-red-200 text-xs font-semibold text-red-500
                hover:bg-red-50 hover:border-red-300 transition-colors"
            >
              <Trash2 size={13} /> Discard All
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setPhase("prompt");
                  setSequence(null);
                  setSteps([]);
                }}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full
                  border border-black/[0.08] text-sm font-semibold text-slate
                  hover:border-black/20 transition-colors"
              >
                <Sparkles size={14} /> Generate Another
              </button>
              <button
                onClick={() => navigate("/campaigns")}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full
                  bg-coral text-white font-semibold text-sm
                  hover:bg-coral-dark shadow-md shadow-coral/20 transition-all"
              >
                Go to Campaigns <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
      `}</style>
    </div>
  );
}
